import { IonButton } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../ui/Avatar';
import {
  useAcceptConnection,
  useMyConnections,
  useRejectConnection,
} from '../../hooks/useConnections';

/**
 * Incoming connection requests (`requests_to_me`) with Accept / Decline.
 * Renders nothing when there are no pending requests, so it only appears on
 * the Profile when there's something to act on.
 *
 * "Who's asking" comes from the preloaded `connection.profile` (the
 * initiator) — the list endpoint preloads it, so no per-row fetch.
 */
export const ConnectionRequests: React.FC = () => {
  const { t } = useTranslation('connections');
  const { data: requests } = useMyConnections('requests_to_me');
  const accept = useAcceptConnection();
  const reject = useRejectConnection();

  if (!requests || requests.length === 0) return null;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <h3 className="font-medium">{t('requests.title')}</h3>
      <ul className="divide-y divide-border">
        {requests.map((c) => {
          // A row is busy while either mutation is in flight for its id —
          // disable both buttons on that row, leave other rows interactive.
          const isBusy =
            (accept.isPending && accept.variables === c.id) ||
            (reject.isPending && reject.variables === c.id);
          const name = c.profile?.displayName || c.profile?.email || t('requests.someone');
          return (
            // Identity on top, Accept/Decline always on their own row below.
            // Two buttons never fit cleanly beside the name across text sizes,
            // so we stack unconditionally rather than wrap.
            <li key={c.id} className="flex flex-col gap-3 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <Avatar src={c.profile?.avatarUrl} name={name} size="md" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{name}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('requests.wantsToConnect')}
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <IonButton
                  size="small"
                  fill="outline"
                  color="medium"
                  onClick={() => reject.mutate(c.id)}
                  disabled={isBusy}
                >
                  {t('requests.decline')}
                </IonButton>
                <IonButton
                  size="small"
                  onClick={() => accept.mutate(c.id)}
                  disabled={isBusy}
                >
                  {t('requests.accept')}
                </IonButton>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
